/**
 * MediFlow Error Monitoring — Lightweight Observability Layer
 *
 * Trackt:
 *   - ChunkLoadError / dynamic import failures
 *   - Asset 404 Rate
 *   - Unhandled Promise Rejections
 *   - Web Vitals (CLS, INP, LCP, TTFB)
 *
 * DSGVO-konform: Keine PHI, keine User-IDs, keine IP-Adressen.
 * Nur technische Fehler-Metadaten + Build-ID für Release-Tracking.
 *
 * Kann später gegen Sentry.io ausgetauscht werden — gleiches Interface.
 */

interface ErrorEvent {
  type: 'chunk_load_error' | 'asset_404' | 'unhandled_error' | 'unhandled_rejection';
  message: string;
  timestamp: number;
  buildId: string;
  url: string;
  userAgent: string;
  meta?: Record<string, unknown>;
}

interface MonitoringConfig {
  /** Supabase-Endpoint für Error-Logging (Edge Function) */
  endpoint?: string;
  /** Build-ID für Release-Tracking */
  buildId: string;
  /** Error-Rate Schwellenwert für Alerts (0-1) */
  alertThreshold?: number;
  /** Ob Errors an Console geloggt werden sollen */
  verbose?: boolean;
}

const ERROR_BUFFER: ErrorEvent[] = [];
const FLUSH_INTERVAL_MS = 30_000; // Alle 30s flushen
const MAX_BUFFER_SIZE = 50;

let config: MonitoringConfig = {
  buildId: 'unknown',
  alertThreshold: 0.001,
  verbose: import.meta.env.DEV,
};

let flushTimer: ReturnType<typeof setInterval> | null = null;
let sessionErrorCount = 0;
let sessionPageViews = 0;

/**
 * Prüft ob ein Fehler ein ChunkLoadError ist.
 */
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('chunkloaderror')
  );
}

/**
 * Sammelt einen Error-Event im Buffer.
 */
function captureEvent(event: ErrorEvent): void {
  sessionErrorCount++;

  if (config.verbose) {
    console.warn(`[Monitoring] ${event.type}:`, event.message, event.meta);
  }

  ERROR_BUFFER.push(event);

  // Sofort flushen bei kritischen Fehlern
  if (event.type === 'chunk_load_error') {
    void flushEvents();
  }

  // Buffer-Overflow-Schutz
  if (ERROR_BUFFER.length >= MAX_BUFFER_SIZE) {
    void flushEvents();
  }
}

/**
 * Sendet gesammelte Events an den Monitoring-Endpoint.
 */
async function flushEvents(): Promise<void> {
  if (ERROR_BUFFER.length === 0) return;

  const events = ERROR_BUFFER.splice(0);

  if (!config.endpoint) {
    // Kein Endpoint konfiguriert — nur lokales Logging
    if (config.verbose) {
      console.log(`[Monitoring] ${events.length} Events (kein Endpoint konfiguriert)`, events);
    }
    return;
  }

  try {
    await fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        buildId: config.buildId,
        events,
        sessionStats: {
          errorCount: sessionErrorCount,
          pageViews: sessionPageViews,
          errorRate: sessionPageViews > 0 ? sessionErrorCount / sessionPageViews : 0,
        },
      }),
      // Nicht blockierend — keepalive für Tab-Close
      keepalive: true,
    });
  } catch {
    // Monitoring darf die App niemals crashen
    if (config.verbose) {
      console.warn('[Monitoring] Flush fehlgeschlagen — Events verworfen');
    }
  }
}

/**
 * Erstellt einen Error-Event aus einem Error-Objekt.
 */
function createEvent(
  type: ErrorEvent['type'],
  error: unknown,
  meta?: Record<string, unknown>,
): ErrorEvent {
  const message = error instanceof Error ? error.message : String(error);
  return {
    type,
    message,
    timestamp: Date.now(),
    buildId: config.buildId,
    url: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    meta,
  };
}

/**
 * Initialisiert das Monitoring-System.
 * Muss einmal beim App-Start aufgerufen werden.
 */
export function initMonitoring(userConfig?: Partial<MonitoringConfig>): void {
  config = { ...config, ...userConfig };

  if (typeof window === 'undefined') return;

  // ── Global Error Handler ──
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error)) {
      captureEvent(createEvent('chunk_load_error', event.error, {
        filename: event.filename,
        lineno: event.lineno,
      }));
    } else {
      captureEvent(createEvent('unhandled_error', event.error ?? event.message, {
        filename: event.filename,
        lineno: event.lineno,
      }));
    }
  });

  // ── Unhandled Promise Rejection ──
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      captureEvent(createEvent('chunk_load_error', event.reason));
    } else {
      captureEvent(createEvent('unhandled_rejection', event.reason));
    }
  });

  // ── Asset 404 Tracking via PerformanceObserver ──
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const resourceEntry = entry as PerformanceResourceTiming;
          // responseStatus 0 = blocked/failed, responseStatus !== 200 = error
          if (
            resourceEntry.initiatorType === 'script' &&
            resourceEntry.responseStatus !== undefined &&
            resourceEntry.responseStatus !== 200 &&
            resourceEntry.name.includes('/assets/')
          ) {
            captureEvent(createEvent('asset_404', new Error(`Asset failed: ${resourceEntry.name}`), {
              status: resourceEntry.responseStatus,
              duration: Math.round(resourceEntry.duration),
            }));
          }
        }
      });
      observer.observe({ type: 'resource', buffered: false });
    } catch {
      // PerformanceObserver nicht unterstützt — unkritisch
    }
  }

  // ── Periodischer Flush ──
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = setInterval(() => void flushEvents(), FLUSH_INTERVAL_MS);

  // ── Flush bei Tab-Close ──
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushEvents();
    }
  });

  // Session PageView Counter
  sessionPageViews++;

  if (config.verbose) {
    console.log(`[Monitoring] Initialisiert (Build: ${config.buildId})`);
  }
}

/**
 * Manuell einen ChunkLoadError tracken.
 */
export function trackChunkLoadError(error: unknown, chunkName?: string): void {
  captureEvent(createEvent('chunk_load_error', error, { chunkName }));
}

/**
 * Aktuelle Error-Rate der Session abfragen.
 */
export function getSessionErrorRate(): { errors: number; pageViews: number; rate: number } {
  return {
    errors: sessionErrorCount,
    pageViews: sessionPageViews,
    rate: sessionPageViews > 0 ? sessionErrorCount / sessionPageViews : 0,
  };
}

/**
 * Increment page view counter (call on route change).
 */
export function trackPageView(): void {
  sessionPageViews++;
}
